// NewRequestModal — port of sampler/src/components/NewRequestModal.vue.
// Title (required) + optional Etsy URL → fetch a listing preview → create. The
// preview is persisted as the first reference by the backend.

import { useState } from 'react'
import { X, Link2, Loader2 } from 'lucide-react'
import { Card } from '../../components/ui/card'
import { createSample, urlPreview } from './api'
import { useT } from '../../i18n'
import { newSampleRequestI18n } from '../../i18n/samples/new-request'

type Preview = {
  title?: string
  description?: string
  price?: string
  thumbnail?: string
  images?: string[]
  photos?: Array<{ url: string }>
  error?: string
  url?: string
}

export function NewRequestModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  onCreated: (sampleId: string) => void
}) {
  const t = useT(newSampleRequestI18n)
  const [title, setTitle] = useState('')
  const [etsyUrl, setEtsyUrl] = useState('')
  const [preview, setPreview] = useState<Preview | null>(null)
  const [fetching, setFetching] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!open) return null

  async function fetchPreview() {
    if (!etsyUrl.trim()) return
    setFetching(true)
    setError(null)
    try {
      const p = (await urlPreview(etsyUrl.trim())) as Preview
      setPreview(p)
      if (p.title && !title.trim()) setTitle(p.title)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setFetching(false)
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!title.trim()) return
    setSaving(true)
    setError(null)
    try {
      const sample = await createSample({
        title: title.trim(),
        etsy_url: etsyUrl.trim() || undefined,
        preview: preview ?? undefined,
      })
      reset()
      onCreated(sample.id)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
      setSaving(false)
    }
  }

  function reset() {
    setTitle('')
    setEtsyUrl('')
    setPreview(null)
    setError(null)
    setSaving(false)
  }

  const thumb = preview?.thumbnail || preview?.images?.[0] || preview?.photos?.[0]?.url

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <Card className="w-full max-w-lg p-5 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-base font-semibold text-[var(--color-text)]">{t.title}</h2>
            <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
              {t.subtitle}
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              reset()
              onClose()
            }}
            className="text-[var(--color-text-muted)] hover:text-[var(--color-text)] p-0.5"
            aria-label={t.close}
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-[var(--color-text)] mb-1">{t.sampleName}</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t.sampleNamePlaceholder}
              className="w-full px-3 py-2 text-sm border border-[var(--color-border-strong)] rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
              required
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-[var(--color-text)] mb-1">{t.etsyUrl}</label>
            <div className="flex items-center gap-2">
              <input
                type="url"
                value={etsyUrl}
                onChange={(e) => setEtsyUrl(e.target.value)}
                placeholder="https://etsy.com/listing/…"
                className="flex-1 px-3 py-2 text-sm border border-[var(--color-border-strong)] rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <button
                type="button"
                onClick={fetchPreview}
                disabled={fetching || !etsyUrl.trim()}
                className="text-xs px-2.5 py-2 bg-[var(--color-surface-muted)] hover:bg-[var(--color-border)] rounded inline-flex items-center gap-1 disabled:opacity-50"
              >
                {fetching ? <Loader2 size={12} className="animate-spin" /> : <Link2 size={12} />}
                {t.fetch}
              </button>
            </div>
          </div>

          {preview && !preview.error && (
            <div className="flex items-start gap-3 p-2.5 border border-[var(--color-border)] rounded bg-[var(--color-surface-muted)]">
              {thumb && (
                <img src={thumb} alt="" className="h-16 w-16 rounded object-cover shrink-0" />
              )}
              <div className="min-w-0 text-xs">
                <div className="font-medium text-[var(--color-text)] truncate">{preview.title || '—'}</div>
                {preview.price && <div className="text-[var(--color-text-muted)] mt-0.5">{preview.price}</div>}
                <div className="text-[var(--color-text-muted)] mt-0.5">
                  {t.photosAttached(preview.photos?.length || preview.images?.length || 0)}
                </div>
              </div>
            </div>
          )}
          {preview?.error && (
            <div className="text-[11px] text-amber-700">{t.previewUnavailable(preview.error)}</div>
          )}

          {error && (
            <div className="p-2 bg-rose-50 border border-rose-200 rounded text-xs text-rose-700">{error}</div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => {
                reset()
                onClose()
              }}
              className="text-xs px-3 py-1.5 border border-[var(--color-border-strong)] rounded hover:bg-[var(--color-surface-muted)]"
            >
              {t.cancel}
            </button>
            <button
              type="submit"
              disabled={saving || !title.trim()}
              className="text-xs px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded disabled:opacity-50"
            >
              {saving ? t.creating : t.create}
            </button>
          </div>
        </form>
      </Card>
    </div>
  )
}
